import {
  isFunction,
  get,
  isEmpty,
  includes,
  filter,
  set,
  isNil,
  merge,
  isEqual,
  map,
  size,
  has,
  find,
  cloneDeep,
  reduce,
} from 'lodash';
import { IPdfDefinitions, IPdfColumnSelector } from '../pdf.type';
import { UiActions } from '@app/store/actions';
import {
  getColumnsFromGrid,
  isSelectedItem,
} from '../utils/pdf-column-selector.utils';
import { pdfModalDefinition } from '../../modal.definition';
import { FormGroup } from '@angular/forms';
import { BehaviorSubject, rx } from '@app/shared/rx';
import { IPdfTableHeader } from '../../pdf-table-header.type';
import { ToolbarButtonDefinition } from '@geniuspos/ui-shared';
import { BTN_RESET } from '@app/text-content/button-templates';
import { I18n } from '@app/i18n/strings-keys';
import { XVII18nService } from '@app/i18n/services';
import {
  SIGNATURE_LINES_MODAL_DEFINITIONS,
  TOTALS_MODAL_DEFINITIONS,
} from '../constants';

const getColumns = (
  columns: any[],
  requiredNotDisabled: boolean,
): IPdfTableHeader[] => {
  return map(columns, (header) => ({
    ...header,
    ...(!requiredNotDisabled
      ? {
          disabled: header.required,
        }
      : {
          disabled:
            has(header, 'defaultDisabled') && get(header, 'defaultDisabled'),
        }),
  })) as IPdfTableHeader[];
};

const getDefaultValues = (columns: any[]): string[] => {
  return reduce(
    columns,
    (result, column) => {
      if (column.required || column.defaultSelected) {
        result.push(column?.text || column?.title || column?.path);
      }

      return result;
    },
    [],
  );
};

/**
 * use this to add ui loader wrap
 * caution: this need uiActions property in your class
 *
 * @param optionsParamPos options params position in the method params list, default is 0
 */
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions,
function popColumnSelector(optionsParamPos: number = 0): MethodDecorator {
  return <T>(
    target: { uiActions: UiActions; i18nService: XVII18nService },
    propertyKey: string | symbol,
    descriptor: TypedPropertyDescriptor<T>,
  ): TypedPropertyDescriptor<T> => {
    const originalMethod = descriptor.value;

    if (!isFunction(originalMethod)) {
      throw new TypeError(
        `@popColumnSelector() decorator can be applied only for methods.`,
      );
    }

    // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
    const wrapper = async function (...params: any[]): Promise<any> {
      const definitions: Partial<IPdfDefinitions> = get(
        params,
        optionsParamPos ?? 0,
      );
      const signatureConfiguration = get(
        definitions,
        ['options', 'columnSelector', 'signatureConfiguration'],
        false,
      );

      const totalsConfiguration = get(
        definitions,
        ['options', 'columnSelector', 'totalsConfiguration'],
        {},
      );

      let headers: IPdfColumnSelector['columns'] = [];
      let showSelectAll: IPdfColumnSelector['showSelectAll'] = true;
      let tips: IPdfColumnSelector['tips'] = '';
      let maxSelectCount: IPdfColumnSelector['maxSelectCount'];
      let showSelectedCount: IPdfColumnSelector['showSelectedCount'];
      let twoColumnLayout: IPdfColumnSelector['twoColumnLayout'];
      let requiredNotDisabled: IPdfColumnSelector['requiredNotDisabled'];
      let supportSelectItemsCache: IPdfColumnSelector['supportSelectItemsCache'];
      let showResetButton: IPdfColumnSelector['showResetButton'];
      let cacheKey: IPdfColumnSelector['cacheKey'];
      let resetButton: ToolbarButtonDefinition = {
        ...BTN_RESET,
        hidden: true,
      };

      let initColumns: any[] = [];

      // eslint-disable-next-line no-invalid-this
      const callOrigin = (): T => originalMethod.apply(this, params);

      const columnSelectorConfig = definitions?.options?.columnSelector;
      // config is empty, just call original method
      if (isEmpty(columnSelectorConfig)) {
        return callOrigin();
      }

      headers = get(columnSelectorConfig, 'columns', []);
      showSelectAll = get(columnSelectorConfig, 'showSelectAll', true);
      tips = get(columnSelectorConfig, 'tips', '');
      // eslint-disable-next-line prefer-const
      maxSelectCount = get(columnSelectorConfig, 'maxSelectCount', 0);
      // eslint-disable-next-line prefer-const
      showSelectedCount = get(columnSelectorConfig, 'showSelectedCount', false);
      // eslint-disable-next-line prefer-const
      twoColumnLayout = get(columnSelectorConfig, 'twoColumnLayout', false);
      // eslint-disable-next-line prefer-const
      requiredNotDisabled = get(
        columnSelectorConfig,
        'requiredNotDisabled',
        false,
      );
      // eslint-disable-next-line prefer-const
      supportSelectItemsCache = get(
        columnSelectorConfig,
        'supportSelectItemsCache',
        false,
      );
      // eslint-disable-next-line prefer-const
      showResetButton = get(columnSelectorConfig, 'showResetButton', false);
      // eslint-disable-next-line prefer-const
      cacheKey = get(columnSelectorConfig, 'cacheKey', '');

      if (
        isEmpty(headers) &&
        !isEmpty(columnSelectorConfig?.columnsFromGridPath)
      ) {
        headers = getColumnsFromGrid(definitions);
      }
      // value of init columns
      initColumns = cloneDeep(headers);
      // deal cache selected columns data
      if (
        supportSelectItemsCache &&
        isFunction(columnSelectorConfig.getCacheSelectItems)
      ) {
        const cacheSelectItems = columnSelectorConfig.getCacheSelectItems();
        if (!isEmpty(cacheSelectItems)) {
          headers = map(headers, (header) => ({
            ...header,
            required: false,
            ...(find(
              cacheSelectItems,
              (selectItem) =>
                isEqual(selectItem.title, header.title) ||
                isEqual(selectItem.path, header.path),
            ) || {}),
          }));
        }
      }

      const columnObs: BehaviorSubject<IPdfTableHeader[]> = new BehaviorSubject<
        IPdfTableHeader[]
      >([]);
      const defaultValuesObs: BehaviorSubject<string[]> = new BehaviorSubject<
        string[]
      >([]);
      if (showResetButton) {
        resetButton = {
          ...BTN_RESET,
          hidden: !showResetButton,
          click: (): void => {
            if (!isEmpty(cacheKey)) {
              // eslint-disable-next-line no-invalid-this
              this.uiActions.savePrintColumns({ [cacheKey]: [] });
            }
            if (isFunction(columnSelectorConfig.resetCallback)) {
              columnSelectorConfig.resetCallback([]);
            }
            columnObs.next(getColumns(initColumns, requiredNotDisabled));
            defaultValuesObs.next(getDefaultValues(initColumns));
          },
        };
      }
      const titleObs: BehaviorSubject<string> = new BehaviorSubject<string>(
        I18n.Common.SelectColumns,
      );
      columnObs.next(getColumns(headers, requiredNotDisabled));
      defaultValuesObs.next(getDefaultValues(headers));

      // Determine default value for totals
      let isTotalsEnabled = false; // whether to show totals toggle
      let totalsDefaultValue = true; // default to true

      if (!isEmpty(totalsConfiguration)) {
        isTotalsEnabled = get(totalsConfiguration, 'enabled', false);

        // Use custom default value getter if provided
        const pdfData = definitions?.data;

        const getDefaultValueFn = get(
          totalsConfiguration,
          'getDefaultValue',
          undefined,
        ) as ((pdfData: unknown) => boolean) | undefined;

        if (isFunction(getDefaultValueFn)) {
          totalsDefaultValue = getDefaultValueFn(pdfData);
        }
      }

      // Merge signature and totals configurations
      const additionalModalOptions = merge(
        {},
        isTotalsEnabled
          ? {
              ...TOTALS_MODAL_DEFINITIONS,
              value: {
                ...TOTALS_MODAL_DEFINITIONS.value,
                totals: totalsDefaultValue,
              },
            }
          : {},
        signatureConfiguration ? SIGNATURE_LINES_MODAL_DEFINITIONS : {},
      );

      const modalOptions = pdfModalDefinition.selectPdfTableHeadersModal(
        columnObs as any,
        defaultValuesObs,
        {
          tips,
          showSelectAll,
          titleObs,
          twoColumnLayout,
          requiredNotDisabled,
          resetButton,
          // new signatureLines and totals configuration
          additionalModalOptions,
        },
      );
      // eslint-disable-next-line no-invalid-this
      const defaultDisabledSize = size(
        filter(headers, (header) => header.required),
      );
      // eslint-disable-next-line no-invalid-this
      const dialogResult = await this.uiActions.promptFormModal(
        maxSelectCount
          ? merge(modalOptions, {
              onFormReady: (form: FormGroup): void => {
                form.controls.selected_columns.valueChanges
                  .pipe(rx.distinctUntilChanged())
                  .subscribe((selectedValue: string[]) => {
                    if (
                      supportSelectItemsCache &&
                      isFunction(columnSelectorConfig.onCacheSelectItems)
                    ) {
                      columnSelectorConfig.onCacheSelectItems(
                        filter(initColumns, (header) =>
                          isSelectedItem(selectedValue, selectedValue, header),
                        ),
                        // eslint-disable-next-line no-invalid-this
                        this.uiActions,
                      );
                    }
                    if (showSelectedCount) {
                      titleObs.next(
                        `${this.i18nService.translate(I18n.Common.SelectColumns)} (${size(selectedValue)}/${maxSelectCount})`,
                      );
                    }
                    if (
                      isEqual(
                        size(selectedValue),
                        !requiredNotDisabled
                          ? defaultDisabledSize + maxSelectCount
                          : maxSelectCount,
                      )
                    ) {
                      columnObs.next(
                        map(headers, (column: IPdfTableHeader) => ({
                          ...column,
                          disabled: !requiredNotDisabled
                            ? !includes(selectedValue, column.title)
                            : (has(column, 'defaultDisabled') &&
                                get(column, 'defaultDisabled')) ||
                              !includes(selectedValue, column.title),
                        })),
                      );
                    } else {
                      columnObs.next(
                        map(headers, (column: IPdfTableHeader) => ({
                          ...column,
                          disabled: !requiredNotDisabled
                            ? false
                            : has(column, 'defaultDisabled') &&
                              get(column, 'defaultDisabled'),
                        })),
                      );
                    }
                  });
              },
            })
          : modalOptions,
      );
      const { confirmed, value } = dialogResult;
      if (!confirmed) {
        return;
      }

      const selectedColumns = filter(initColumns, (header) =>
        isSelectedItem(value.selected_columns, value.selected_columns, header),
      );

      let newSelectedColumns;

      if (isFunction(columnSelectorConfig.onClose)) {
        newSelectedColumns = columnSelectorConfig.onClose(selectedColumns);
      }
      // use new selected columns when new selected Columns is no undefined or null
      set(
        definitions,
        'options.selectedColumns',
        !isNil(newSelectedColumns) ? newSelectedColumns : selectedColumns,
      );

      // when print, default behavior is show signature
      // when you off switch or config it as false will not show signature.
      set(
        definitions,
        'options.signature_lines',
        get(value, 'signature_lines', true),
      );

      // when print, default behavior is show totals
      // when you off switch or config it as false will not show totals.
      set(definitions, 'options.totals', get(value, 'totals', true));

      return callOrigin();
    };

    return {
      ...descriptor,
      value: wrapper as any,
    };
  };
}

export { popColumnSelector };
